import React, { useState } from "react";
import styled from "styled-components";
import { Edit2, Plus, Trash2 } from "lucide-react";

import NavigationBar from "../../../components/nav/NavigationBar.tsx";

const Container = styled.div`
  align-items: stretch;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  // gap: 0px;
  justify-content: flex-start;
  padding: clamp(40px, 10vw, 80px) 0 0 0;
  position: relative;
`;

const PageContainer = styled.div`
  padding: 20px;
  font-family: 'Inter', sans-serif;
  background-color: #f9fafb;
  min-height: 100vh;
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const SearchBar = styled.input`
  width: 70%;
  padding: 10px;
  font-size: 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background-color: #ffffff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2);
  }
`;

const CreateButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  font-size: 16px;
  background-color: #3b82f6;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #2563eb;
  }
`;

const TableContainer = styled.div`
  overflow-x: auto;
  margin-top: 20px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  border-radius: 12px;
  overflow: hidden;

  th,
  td {
    padding: 15px;
    text-align: left;
    font-size: 14px;
  }

  th {
    background-color: #f3f4f6;
    color: #4b5563;
    font-weight: 600;
    text-transform: uppercase;
    font-size: 12px;
    border-bottom: 2px solid #e5e7eb;
  }

  tbody tr {
    border-bottom: 1px solid #f3f4f6;
    transition: background-color 0.2s ease;
  }

  tbody tr:hover {
    background-color: #f9fafb;
  }

  td {
    color: #374151;
  }

  tr:nth-child(even) {
    background-color: #f9fafb;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 10px;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  font-size: 14px;
  color: #374151;
  border: none;
  border-radius: 6px;
  background-color: #e5e7eb;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #d1d5db;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  padding: 20px;
  border-radius: 12px;
  width: 400px;
  max-width: 90%;
  box-shadow: 0px 8px 16px rgba(0, 0, 0, 0.1);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;

  h2 {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    color: #111827;
  }

  button {
    background: none;
    border: none;
    font-size: 18px;
    cursor: pointer;
    color: #6b7280;
  }
`;

const FormField = styled.div`
  margin-bottom: 15px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  font-size: 14px;
  color: #374151;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  font-size: 14px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background-color: #f9fafb;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 10px;
  font-size: 14px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background-color: #f9fafb;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2);
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const SubmitButton = styled.button`
  padding: 10px 20px;
  font-size: 14px;
  background-color: #10b981;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #059669;
  }
`;

const CancelButton = styled.button`
  padding: 10px 20px;
  font-size: 14px;
  background-color: #ef4444;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #dc2626;
  }
`;

type Licensee = {
  id: number;
  name: string;
  active: boolean;
  option: string;
  mode: string;
  createdOn: string;
  lastModified: string;
  createdBy: string;
  requiresReview: boolean;
  volume: number;
};

const initialLicensees: Licensee[] = [
  {
    id: 1,
    name: "Licensee A",
    active: true,
    option: "Manual",
    mode: "Unit",
    createdOn: "2023-01-01",
    lastModified: "2023-01-10",
    createdBy: "Admin",
    requiresReview: false,
    volume: 500,
  },
  {
    id: 2,
    name: "Licensee B",
    active: false,
    option: "Automatic",
    mode: "Volume",
    createdOn: "2023-02-01",
    lastModified: "2023-02-15",
    createdBy: "User1",
    requiresReview: true,
    volume: 1200,
  },
];

const PaymentProcessing: React.FC = () => {
  const [licensees, setLicensees] = useState<Licensee[]>(initialLicensees);
  const [searchQuery, setSearchQuery] = useState("");
  const [isModalOpen, setModalOpen] = useState(false);
  const [formData, setFormData] = useState<Licensee | null>(null);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const filteredLicensees = licensees.filter((licensee) =>
    licensee.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleOpenEditModal = (licensee: Licensee) => {
    setFormData(licensee);
    setModalOpen(true);
  };

  const handleOpenCreateModal = () => {
    setFormData({
      id: 0,
      name: "",
      active: false,
      option: "Manual",
      mode: "Unit",
      createdOn: new Date().toISOString().split("T")[0],
      lastModified: new Date().toISOString().split("T")[0],
      createdBy: "Admin",
      requiresReview: false,
      volume: 0,
    });
    setModalOpen(true);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    if (type === "checkbox") {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({ ...prev!, [name]: checked }));
    } else {
      setFormData((prev) => ({ ...prev!, [name]: value }));
    }
  };

  const handleSubmit = () => {
    if (formData) {
      if (formData.id === 0) {
        // Create new licensee
        setLicensees((prev) => [
          ...prev,
          { ...formData, id: licensees.length + 1 },
        ]);
      } else {
        // Edit existing licensee
        setLicensees((prev) =>
          prev.map((item) =>
            item.id === formData.id
              ? { ...formData, lastModified: new Date().toISOString().split("T")[0] }
              : item
          )
        );
      }
      setModalOpen(false);
    }
  };

  return (
    <Container>
      <NavigationBar />
      <PageContainer>
        <HeaderContainer>
          <SearchBar
            type="text"
            placeholder="Search licensees..."
            value={searchQuery}
            onChange={handleSearch}
          />
          <CreateButton onClick={handleOpenCreateModal}>
            <Plus />
            Create New
          </CreateButton>
        </HeaderContainer>

        <TableContainer>
          <StyledTable>
            <thead>
              <tr>
                <th>Name</th>
                <th>Active</th>
                <th>Option</th>
                <th>Mode</th>
                <th>Created On</th>
                <th>Last Modified</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredLicensees.map((licensee) => (
                <tr key={licensee.id}>
                  <td>{licensee.name}</td>
                  <td>{licensee.active ? "Yes" : "No"}</td>
                  <td>{licensee.option}</td>
                  <td>{licensee.mode}</td>
                  <td>{licensee.createdOn}</td>
                  <td>{licensee.lastModified}</td>
                  <td>
                    <ActionButtons>
                      <ActionButton onClick={() => handleOpenEditModal(licensee)}>
                        <Edit2 />
                      </ActionButton>
                    </ActionButtons>
                  </td>
                </tr>
              ))}
            </tbody>
          </StyledTable>
        </TableContainer>

        {isModalOpen && formData && (
          <ModalOverlay>
            <ModalContent>
              <ModalHeader>
                <h2>{formData.id === 0 ? "Create Licensee" : "Edit Licensee"}</h2>
                <button onClick={() => setModalOpen(false)}>&times;</button>
              </ModalHeader>
              <FormField>
                <Label>Name</Label>
                <Input
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                />
              </FormField>
              <FormField>
                <Label>Active</Label>
                <CheckboxContainer>
                  <input
                    name="active"
                    type="checkbox"
                    checked={formData.active}
                    onChange={handleChange}
                  />
                  <span>Yes</span>
                </CheckboxContainer>
              </FormField>
              <FormField>
                <Label>Option</Label>
                <Select
                  name="option"
                  value={formData.option}
                  onChange={handleChange}
                >
                  <option value="Manual">Manual</option>
                  <option value="Automatic">Automatic</option>
                </Select>
              </FormField>
              <FormField>
                <Label>Mode</Label>
                <Select
                  name="mode"
                  value={formData.mode}
                  onChange={handleChange}
                >
                  <option value="Unit">Unit</option>
                  <option value="Volume">Volume</option>
                </Select>
              </FormField>
              <FormField>
                <Label>Volume</Label>
                <Input
                  name="volume"
                  value={formData.volume}
                  onChange={handleChange}
                  type="number"
                />
              </FormField>
              <FormField>
                <Label>Created On</Label>
                <Input name="createdOn" value={formData.createdOn} disabled />
              </FormField>
              <FormField>
                <Label>Last Modified</Label>
                <Input name="lastModified" value={formData.lastModified} disabled />
              </FormField>
              <ButtonGroup>
                <CancelButton onClick={() => setModalOpen(false)}>Cancel</CancelButton>
                <SubmitButton onClick={handleSubmit}>Save</SubmitButton>
              </ButtonGroup>
            </ModalContent>
          </ModalOverlay>
        )}
      </PageContainer>
    </Container>
    );
};

export default PaymentProcessing;